import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import BaseModal from "./BaseModal";
import Button from "../forms/Button";

const VIEWPORT = 288; // px, square crop area shown to the user
const OUTPUT = 600; // px, side of the exported square image
const MAX_ZOOM = 4;

const clampOffset = (offset, displayed) => {
  const limit = Math.max(0, (displayed - VIEWPORT) / 2);
  return Math.min(limit, Math.max(-limit, offset));
};

/**
 * Lets the user pan and zoom an image inside a square frame and returns
 * the visible part as a JPEG File via onConfirm(file).
 */
const ImageCropModal = ({ file, onCancel, onConfirm }) => {
  const [src, setSrc] = useState(null);
  const [natural, setNatural] = useState(null); // { w, h }
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Scale at which the image just covers the square frame.
  const baseScale = natural ? VIEWPORT / Math.min(natural.w, natural.h) : 1;
  const scale = baseScale * zoom;
  const dispW = natural ? natural.w * scale : 0;
  const dispH = natural ? natural.h * scale : 0;

  const applyZoom = (nextZoom) => {
    const z = Math.min(MAX_ZOOM, Math.max(1, nextZoom));
    const s = baseScale * z;
    setZoom(z);
    setOffset((o) => ({
      x: clampOffset(o.x, natural.w * s),
      y: clampOffset(o.y, natural.h * s),
    }));
  };

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, start: offset };
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset({
      x: clampOffset(d.start.x + e.clientX - d.x, dispW),
      y: clampOffset(d.start.y + e.clientY - d.y, dispH),
    });
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleWheel = (e) => {
    if (!natural) return;
    applyZoom(zoom - e.deltaY * 0.002);
  };

  const handleConfirm = async () => {
    if (!natural || !imgRef.current) return;
    setIsSaving(true);
    try {
      // Frame position expressed in source-image pixels.
      const sw = VIEWPORT / scale;
      const sx = (dispW / 2 - VIEWPORT / 2 - offset.x) / scale;
      const sy = (dispH / 2 - VIEWPORT / 2 - offset.y) / scale;

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      canvas
        .getContext("2d")
        .drawImage(imgRef.current, sx, sy, sw, sw, 0, 0, OUTPUT, OUTPUT);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.9),
      );
      if (!blob) throw new Error("Could not crop this image.");

      const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
      await onConfirm(new File([blob], name, { type: "image/jpeg" }));
    } catch (err) {
      console.error("Crop failed:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseModal
      isOpen={!!file}
      onClose={isSaving ? () => {} : onCancel}
      title="Crop photo"
      subtitle="Drag to reposition, zoom to resize."
      size="sm"
      portal
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!natural || isSaving}>
            {isSaving ? "Uploading..." : "Save photo"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-5">
        <div
          className="relative overflow-hidden rounded-2xl bg-black select-none cursor-grab active:cursor-grabbing"
          style={{ width: VIEWPORT, height: VIEWPORT, touchAction: "none" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt="Crop preview"
              draggable={false}
              onLoad={(e) =>
                setNatural({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                })
              }
              className="absolute max-w-none pointer-events-none"
              style={{
                width: dispW || undefined,
                height: dispH || undefined,
                left: VIEWPORT / 2 - dispW / 2 + offset.x,
                top: VIEWPORT / 2 - dispH / 2 + offset.y,
                opacity: natural ? 1 : 0,
              }}
            />
          )}
          {/* Circular mask preview: dims everything outside the avatar circle */}
          <div
            className="absolute inset-0 pointer-events-none rounded-full"
            style={{
              boxShadow: "0 0 0 999px rgba(0,0,0,0.55)",
              border: "2px solid rgba(255,255,255,0.8)",
            }}
          />
        </div>

        <div className="flex items-center gap-3 w-full max-w-[288px]">
          <ZoomOut className="w-4 h-4 text-muted shrink-0" />
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            disabled={!natural}
            onChange={(e) => applyZoom(parseFloat(e.target.value))}
            aria-label="Zoom"
            className="w-full accent-accent"
          />
          <ZoomIn className="w-4 h-4 text-muted shrink-0" />
        </div>
      </div>
    </BaseModal>
  );
};

export default ImageCropModal;
